import React, { useCallback, useContext, useMemo, useState } from 'react';
import { DocumentNode, useQuery } from '@apollo/client';
import { default as Form } from '../../components/connectedForm';
import * as input from '../../components/connectedForm/inputComponents';
import getIn from '../../components/connectedForm/utils/getIn';
import { InfoButton } from '../../components/connectedForm/Button';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import { useDebouncedTemporarySave } from '../../components/connectedForm/utils/handleTemporarySave';
import { useDispatch } from 'react-redux';
import { Glyph } from '../../components/view/ColumnSelector';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import setIn from '../../components/connectedForm/utils/setIn';
import {
  CustomerContactLabel,
  CustomerContactLetter,
  CustomerContactMailshot,
} from '../../graphql/entities/customer/CustomerContact';
import { ContactDocumentModal } from './CustomerContactWorkspace';
import ReferenceTableDropdown from '../../components/connectedForm/ReferenceTableDropdown';
import { JobTitle } from '../../graphql/entities';
import {
  CustomerContactDeliveryType,
  ICustomer,
  ICustomerContact,
  ICustomerGlobalContact,
  ISupplier,
  ISupplierContact,
} from '../../graphql/schema/p4types';
import { EntityType } from '../../@types/entityTypes';
import { IContextualMenuItem } from '@fluentui/react';
import DocumentCheckboxTree from '../../components/connectedForm/DocumentCheckboxTree';
import {
  BlurTextFieldType,
  HrefLink,
  IBlurTextFieldProps,
} from '../../components/connectedForm/BlurTextField/BlurTextField';
import { IDropdownProps } from '../../@types/dropdown';
import { IAttributeArrayProps } from '../../components/connectedForm/AttributeArray';
import { ButtonContainerDiv } from '../../components/modals/ButtonContainerDiv';
import { useCompany } from '../../utils/FetchConfig';
import CustomerGlobalContactWorkspace from './CustomerGlobalContactWorkspace';
import CustomerGlobalContact from '../../graphql/entities/customer/CustomerGlobalContact';
import CustomerGlobalContactForm from './CustomerGlobalContactForm';
import { ConnectedAddress } from '../../components/connectedForm/ConnectedAddress';
import FluentModal from '../../components/modals/FluentModal';

const EditGlobalContactAction = ({
  dataid,
  customerContact,
}: {
  dataid: string;
  customerContact: ICustomerContact;
}) => {
  const dispatch = useDispatch();
  const connectedForm = useContext(ConnectedFormContext);
  const [editGlobalContactOpen, setEditGlobalContactOption] = useState(false);

  const globalContactId = customerContact?.customerGlobalContact?.id ?? 'New';

  return (
    <>
      <FluentModal isOpen={editGlobalContactOpen}>
        <div style={{ height: '97vh', width: '80vw' }}>
          <CustomerGlobalContactForm
            overrideParams={{ id: globalContactId }}
            onClose={(result) => {
              if (result?.id && globalContactId === 'New') {
                //If we've generated a new global contact, link it.
                dispatch(connectedForm.change(`${dataid}.customerGlobalContact`, result));
              }
              setEditGlobalContactOption(false);
            }}
            modalClose={true}
            defaultVariables={globalContactId === 'New' ? { contactId: customerContact?.id } : undefined}
          />
        </div>
      </FluentModal>
      <InfoButton
        text={`${globalContactId === 'New' ? 'Add New' : 'Edit/View'} Global Contact`}
        onClick={() => {
          setEditGlobalContactOption(true);
        }}
      />
    </>
  );
};

const ContactActionButtons = ({
  dataid,
  type,
  value,
  updatePrimaryContact,
}: {
  dataid: string;
  type: 'customer' | 'supplier';
  value: ICustomerContact | ISupplierContact;
  updatePrimaryContact: () => void;
}) => {
  const [letterModalOpen, setLetterModalOpen] = useState(false);
  const [mailshotModalOpen, setMailshotModalOpen] = useState(false);
  const [labelModalOpen, setLabelModalOpen] = useState(false);

  const printButtonItems: Array<IContextualMenuItem> = [];
  printButtonItems.push({
    key: 'printLetter',
    text: 'Letter',
    onClick: () => setLetterModalOpen(true),
  });
  if ((value as ICustomerContact)?.allowMarketing && value?.email) {
    printButtonItems.push({
      key: 'printMailshot',
      text: 'Mailshot',
      onClick: () => setMailshotModalOpen(true),
    });
  }
  printButtonItems.push({ key: 'printLabel', text: 'Label', onClick: () => setLabelModalOpen(true) });

  const customerPrintActions =
    type === 'customer' ? (
      <>
        <ContactDocumentModal
          modalOpen={letterModalOpen}
          setModalOpen={setLetterModalOpen}
          entity={CustomerContactLetter}
          customerContact={value as ICustomerContact}
          multiValue={false}
        />
        <ContactDocumentModal
          modalOpen={mailshotModalOpen}
          setModalOpen={setMailshotModalOpen}
          entity={CustomerContactMailshot}
          customerContact={value as ICustomerContact}
          multiValue={false}
        />
        <ContactDocumentModal
          modalOpen={labelModalOpen}
          setModalOpen={setLabelModalOpen}
          entity={CustomerContactLabel}
          customerContact={value as ICustomerContact}
          multiValue={false}
        />
        <InfoButton
          text={'Send/Print'}
          menuProps={{
            items: printButtonItems,
          }}
        />
        <EditGlobalContactAction customerContact={value as ICustomerContact} dataid={dataid} />
      </>
    ) : null;

  return (
    <Form.Row>
      <ButtonContainerDiv>
        {value?.primaryContact ? null : <InfoButton onClick={updatePrimaryContact}>Set Primary</InfoButton>}
        {customerPrintActions}
      </ButtonContainerDiv>
    </Form.Row>
  );
};

/**
 * @param dataid - For the name field.
 * @param customerContactDataId - For the contact - When selecting a global customer contact, this is used to update the redux store directly.
 *                                                - Only valid for CUSTOMER contacts. (No SupplierContact support at this time).
 */
export const FullNameField = ({
  dataid,
  enableCustomerGlobalContactGlyph,
}: {
  dataid: string;
  enableCustomerGlobalContactGlyph?: boolean;
}) => {
  const nameDataid = dataid !== '' ? `${dataid}.name` : 'name';
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [globalContactModalOpen, setGlobalContactModalOpen] = useState(false);
  const performTemporarySave = useDebouncedTemporarySave();

  return (
    <>
      <Form.Field<IBlurTextFieldProps>
        dataid={`${nameDataid}.fullName`}
        component={input.BlurTextField}
        label="Name"
        onBlur={(event, newValue) => {
          let newFirstName, newLastName, newMiddleName, newTitle;

          if (!newValue) {
            return;
          }

          let splitName = newValue.split(' ');
          if (splitName.length === 0) return;

          const firstSegment = splitName[0];
          const titleIndex = fieldMetaData.Title.enumValues.findIndex((enumValue) => {
            return firstSegment.toUpperCase() === enumValue.value;
          });
          if (titleIndex >= 0) {
            newTitle = fieldMetaData.Title.enumValues[titleIndex].value;
            splitName = splitName.splice(1);
            if (splitName.length === 1) {
              //We have a title and a single name, assume it's the last name.
              newLastName = splitName[0];
            } else if (splitName.length > 0) {
              //Multiple following names, assume it's the first name.
              newFirstName = splitName[0];
            }
          } else {
            newFirstName = firstSegment;
          }
          splitName = splitName.splice(1);
          if (splitName.length === 1) {
            newLastName = splitName[0];
          } else if (splitName.length > 0) {
            newLastName = splitName[splitName.length - 1];
            splitName = splitName.splice(0, splitName.length - 1);
            newMiddleName = splitName.join(' ');
          }

          let newName = getIn(connectedForm.values, nameDataid);
          newName = setIn(newName, 'title', newTitle || null);
          newName = setIn(newName, 'firstName', newFirstName || null);
          newName = setIn(newName, 'middleName', newMiddleName || null);
          newName = setIn(newName, 'lastName', newLastName || null);

          Promise.all([dispatch(connectedForm.change(nameDataid, newName))]).then(performTemporarySave);
        }}
        inputSuffix={
          <>
            <Glyph
              display={true}
              onClick={() => setNameModalOpen(true)}
              colour={'#686868'}
              icon={'address-card'}
              style={{ marginTop: '0px', marginLeft: '-76px', marginBottom: '10px' }}
            />
            {enableCustomerGlobalContactGlyph ? (
              <Glyph
                display={true}
                onClick={() => setGlobalContactModalOpen(true)}
                colour={'#686868'}
                icon={'search'}
                style={{ marginTop: '0px', marginLeft: '-42px', marginBottom: '10px' }}
              />
            ) : null}
          </>
        }
      />
      <FluentModal isOpen={nameModalOpen} styles={{ main: { minWidth: '600px' } }}>
        <Form.HeaderBlock
          name={'Name Breakdown'}
          onCancel={() => setNameModalOpen(false)}
          onSubmit={() => {
            const newFullName: Array<string> = [];

            const title = getIn(connectedForm.values, `${nameDataid}.title`);
            const firstName = getIn(connectedForm.values, `${nameDataid}.firstName`);
            const middleName = getIn(connectedForm.values, `${nameDataid}.middleName`);
            const lastName = getIn(connectedForm.values, `${nameDataid}.lastName`);

            const titleIndex = fieldMetaData.Title.enumValues.findIndex((enumValue) => enumValue.value === title);
            if (titleIndex >= 0 && fieldMetaData.Title.enumValues[titleIndex].value) {
              //If there's a title, and it is not null.
              newFullName.push(fieldMetaData.Title.enumValues[titleIndex].label);
            }
            if (firstName && firstName.length > 0) newFullName.push(firstName);
            if (middleName && middleName.length > 0) newFullName.push(middleName);
            if (lastName && lastName.length > 0) newFullName.push(lastName);

            Promise.all([dispatch(connectedForm.change(`${nameDataid}.fullName`, newFullName.join(' ')))]).then(
              performTemporarySave
            );

            setNameModalOpen(false);
          }}
          noDeleteOption
          isModal={true}
          saveOptions={[
            {
              value: 'EXIT',
              label: 'Update',
            },
          ]}
        />
        <div style={{ paddingTop: '1em', paddingBottom: '1em' }}>
          <Form.Fieldset cols={1}>
            <Form.Field dataid={`${nameDataid}.title`} component={input.Dropdown} label="Title" />
            <Form.Field<IBlurTextFieldProps>
              dataid={`${nameDataid}.firstName`}
              component={input.BlurTextField}
              label="First Name"
            />
            <Form.Field<IBlurTextFieldProps>
              dataid={`${nameDataid}.middleName`}
              component={input.BlurTextField}
              label="Middle Name"
            />
            <Form.Field<IBlurTextFieldProps>
              dataid={`${nameDataid}.lastName`}
              component={input.BlurTextField}
              label="Last Name"
            />
          </Form.Fieldset>
        </div>
      </FluentModal>
      {enableCustomerGlobalContactGlyph ? (
        <FluentModal isOpen={globalContactModalOpen}>
          <div style={{ position: 'relative', height: '97vh', width: '80vw' }}>
            <CustomerGlobalContactWorkspace
              viewScreenName={`${CustomerGlobalContact.workspaceUrl}Modal`}
              onRowClick={(value: ICustomerGlobalContact) => {
                //Shallow remove id/uuid/concurrencyVersion from the contact record to clone it.
                const { id, uuid, concurrencyVersion, attributeValues, ...clonedContact } = value;
                const customerContact: ICustomerContact = {
                  ...(clonedContact as ICustomerContact),
                  //Also need to strip id/uuid/concurrencyVersion from the attributeValues.
                  attributeValues:
                    attributeValues?.map(
                      ({ id, uuid, concurrencyVersion, ...attributeValue }: any) => attributeValue
                    ) ?? [],
                  customerGlobalContact: value,
                };
                dispatch(connectedForm.change(dataid, customerContact));
                setGlobalContactModalOpen(false);
                performTemporarySave();
              }}
              onClose={() => {
                setGlobalContactModalOpen(false);
              }}
              noAddOption={true}
            />
          </div>
        </FluentModal>
      ) : null}
    </>
  );
};

export const ContactAddress = ({ dataid: contactDataId }: { dataid: string }) => {
  const { values } = useContext(ConnectedFormContext);
  const useMainAddress = getIn(values, `${contactDataId}.useMainAddress`);
  const contactDataIdDot = contactDataId === '' ? '' : `${contactDataId}.`;

  return (
    <>
      <Form.Field
        dataid={`${contactDataIdDot}useMainAddress`}
        component={input.Checkbox}
        label={'Use Main Address'}
        temporary={true}
      />
      <ConnectedAddress dataid={`${contactDataIdDot}address`} readOnly={useMainAddress} />
    </>
  );
};

export const ContactMarketingSection = ({ dataid, type }: { dataid: string; type: 'customer' | 'supplier' }) => {
  if (type !== 'customer') return null;

  const dataIdDot = dataid?.length > 0 ? `${dataid}.` : '';

  return (
    <>
      <Form.Legend label="Marketing" />
      <Form.Field dataid={`${dataIdDot}allowPostalMail`} label={'Allow Direct Marketing'} component={input.Checkbox} />
      <Form.Field dataid={`${dataIdDot}allowMarketing`} label={'Allow Email Marketing'} component={input.Checkbox} />
      <Form.Field dataid={`${dataIdDot}allowPhoneCalls`} label={'Allow Phone Calls'} component={input.Checkbox} />
      <Form.Field dataid={`${dataIdDot}allowFax`} label={'Allow Fax'} component={input.Checkbox} />
    </>
  );
};

export type IContactTabProps = {
  index: number;
  dataid: string;
  type: 'customer' | 'supplier';
  values: ICustomer | ISupplier;
  change: Function;
  value: ICustomerContact | ISupplierContact;
  custom?: {
    type: 'supplier';
  };
  attributeConfig: EntityType;
  readOnly?: boolean;
};

const ContactTab = ({
  index,
  dataid,
  type,
  values: parentRecord,
  change,
  value,
  custom,
  attributeConfig,
  readOnly,
}: IContactTabProps) => {
  const dispatch = useDispatch();
  const company = useCompany();

  /*Dispatch a change to update the primary contact for the Customer/Supplier.*/
  const updatePrimaryContact = () => {
    const contacts = parentRecord?.contacts;
    const newContacts = contacts?.map((newContact: any, idx: number) => {
      const updatedContact = { ...newContact } as ICustomerContact | ISupplierContact;
      updatedContact.primaryContact = idx === index;
      return updatedContact;
    });

    dispatch(change(`contacts`, newContacts));
  };

  /*Additional code for updating delivery type to do additional work (Clear 'primary' from any other contacts for delivery type)*/
  const updateDeliveryType = (event: any, newValue: CustomerContactDeliveryType) => {
    if (newValue === CustomerContactDeliveryType.Primary) {
      const contacts = (parentRecord?.contacts ?? []) as Array<ICustomerContact>;
      const newContacts = contacts.map((newContact, idx) => {
        if (newContact.deliveryType === CustomerContactDeliveryType.Primary && idx !== index) {
          const updatedContact = { ...newContact };
          updatedContact.deliveryType = CustomerContactDeliveryType.Allowed;
          return updatedContact;
        }
        return newContact;
      });

      dispatch(change(`contacts`, newContacts));
    }
  };

  let allowPhoneCalls = (value as ICustomerContact)?.allowPhoneCalls;
  let allowFax = (value as ICustomerContact)?.allowFax;
  if (custom?.type === 'supplier') {
    allowPhoneCalls = true;
    allowFax = true;
  }

  return (
    <>
      <Form.Row>
        <Form.Fieldset cols={1 / 2}>
          <Form.Legend
            label="Address Information"
            description={
              (value as ICustomerContact)?.customerGlobalContact
                ? 'Global Contact - Changes will not be copied to the global contact record automatically.'
                : undefined
            }
          />
          <FullNameField dataid={dataid} enableCustomerGlobalContactGlyph={type === 'customer'} />
          <Form.Field<IDropdownProps & { entity: EntityType; fieldName: string; saveParameter: string }>
            dataid={`${dataid}.jobTitle`}
            component={ReferenceTableDropdown}
            label="Job Title"
            entity={JobTitle}
            fieldName="description"
            saveParameter="jobTitle"
            {...JobTitle.dropdown}
          />
          <ContactAddress dataid={dataid} />
          {type === 'customer' ? (
            <Form.Field<IDropdownProps>
              dataid={`${dataid}.deliveryType`}
              component={input.Dropdown}
              label="Delivery Type"
              onChange={updateDeliveryType}
              enum={true}
              options={fieldMetaData.CustomerContactDeliveryType.enumValues}
            />
          ) : null}
          {company?.webEnabled ? (
            <>
              <Form.Legend label="Web" />
              <Form.Field dataid={`${dataid}.webLogin`} component={input.Checkbox} label="Web Login" />
            </>
          ) : null}
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 2}>
          <Form.Legend label="Contact Information" />
          <Form.Field<IBlurTextFieldProps>
            dataid={`${dataid}.email`}
            component={input.BlurTextField}
            label="Email"
            hyperlink={HrefLink.MailTo}
            labelCols={4 / 12}
            inputCols={8 / 12}
          />
          <Form.Field<IBlurTextFieldProps>
            dataid={`${dataid}.telephoneNumber`}
            component={input.BlurTextField}
            label="Telephone Number"
            type={allowPhoneCalls ? BlurTextFieldType.Text : BlurTextFieldType.Hide}
            hint={allowPhoneCalls ? undefined : 'DO NOT CONTACT'}
            hyperlink={HrefLink.Tel}
            labelCols={4 / 12}
            inputCols={8 / 12}
          />
          <Form.Field<IBlurTextFieldProps>
            dataid={`${dataid}.mobileNumber`}
            component={input.BlurTextField}
            label="Mobile Number"
            type={allowPhoneCalls ? BlurTextFieldType.Text : BlurTextFieldType.Hide}
            hint={allowPhoneCalls ? undefined : 'DO NOT CONTACT'}
            hyperlink={HrefLink.Tel}
            labelCols={4 / 12}
            inputCols={8 / 12}
          />
          <Form.Field<IBlurTextFieldProps>
            dataid={`${dataid}.faxNumber`}
            component={input.BlurTextField}
            label="Fax Number"
            type={allowFax ? BlurTextFieldType.Text : BlurTextFieldType.Hide}
            hint={allowFax ? undefined : 'DO NOT CONTACT'}
            hyperlink={HrefLink.Tel}
            labelCols={4 / 12}
            inputCols={8 / 12}
          />
          <ContactActionButtons type={type} value={value} updatePrimaryContact={updatePrimaryContact} dataid={dataid} />
          <ContactMarketingSection dataid={dataid} type={type} />
          <Form.Row>
            <Form.Fieldset cols={1}>
              {!readOnly ? (
                <>
                  <Form.Legend label={'Archive'} />{' '}
                  <Form.Field dataid={`${dataid}.deleted`} label={'Archive Contact '} component={input.Checkbox} />
                </>
              ) : null}
            </Form.Fieldset>
          </Form.Row>
        </Form.Fieldset>
      </Form.Row>
      <Form.Legend label="Attributes" />
      <Form.Row>
        <Form.Fieldset cols={1}>
          <Form.Field<IAttributeArrayProps>
            dataid={`${dataid}.attributeValues`}
            component={input.AttributeArray}
            label="Attributes"
            query={attributeConfig.list}
            type={`${type}Contact`}
            initialized={true} //Initialized true on form.field skips running the query on initial load for new contacts.
          />
        </Form.Fieldset>
      </Form.Row>
      {type === 'customer' ? (
        <>
          <Form.Legend label="Additional Instruction Lines" />
          <Form.Row>
            <Form.Fieldset cols={1}>
              <Form.Field<IBlurTextFieldProps>
                dataid={`${dataid}.additionalInstructionLines`}
                component={input.BlurTextField}
                includeLabel={false}
                type={BlurTextFieldType.TextArea}
                height={'55px'}
              />
            </Form.Fieldset>
          </Form.Row>
        </>
      ) : null}
      <Form.Legend label="Documents" />
      <Form.Row>
        <DocumentCheckboxTree disabled={readOnly || !value?.name} contactid={`contacts.${index}`} type={type} />
      </Form.Row>
    </>
  );
};

/**
 * Contact Tab Wrapper - Catches the first blur/change & initiates default fields when this occurs, since we otherwise want to avoid initiating the record.
 */
export default (props: IContactTabProps & { entity: EntityType }) => {
  const connectedForm = useContext(ConnectedFormContext);

  const { dataid, values: parentRecord, value: contact, type, entity } = props;

  const memoizedParentField = useMemo(
    () => parentRecord,
    // Suppressing exhaustive-deps here because the objective of this memoization is to prevent unnecessary re-fetch for unrelated changes to the parent record.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parentRecord?.id, parentRecord?.uuid, parentRecord?.contacts?.length]
  );

  const { data } = useQuery(entity.default as DocumentNode, {
    variables: { [type]: normalizeVariables(memoizedParentField) },
    fetchPolicy: 'network-only',
  });

  const onChange = useCallback(
    (field: string, value: any) => {
      //If we've edited a field and don't yet have a UUID, get the default record and set the field onto it.
      if (!contact?.uuid && field.length > dataid.length) {
        const defaultContact = data?.defaultRecord;
        const fieldSuffix = field.replace(`${dataid}.`, '');
        const newContact = setIn(defaultContact, fieldSuffix, value);
        return connectedForm.change(dataid, newContact);
      }
      //Otherwise, just directly update the field.
      return connectedForm.change(field, value);
    },
    [connectedForm, contact?.uuid, data?.defaultRecord, dataid]
  );

  const contactContext = useMemo(() => {
    return { ...connectedForm, change: onChange as any, blur: onChange };
  }, [connectedForm, onChange]);

  return (
    <ConnectedFormContext.Provider value={contactContext}>
      <ContactTab {...props} />
    </ConnectedFormContext.Provider>
  );
};
